import React, { useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Printer as PrinterIcon, Palette as PaletteIcon, Clock as ClockIcon, MessageSquare as MessageSquareIcon, Package as PackageIcon, PenTool as PencilRulerIcon, MessageCircle as MessageCircleIcon, CheckCircle2 as CheckCircle2Icon, Mail as MailIcon, ChevronDown as ChevronDownIcon, ArrowRight as ArrowRightIcon, BoxIcon, Layers as LayersIcon, Maximize as MaximizeIcon, Cpu as CpuIcon, Copy as CopyIcon, Wrench as WrenchIcon } from 'lucide-react';
// --- Sub-components ---
const benefits = ['Free Initial Consultation', 'Low Hourly Print Cost', 'Expert Guidance', 'Local Delivery Available'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
function BenefitsStrip() {
  return <div className="w-full border-b border-sandev-border bg-sandev-card" data-id="element-475">
      <div className="max-w-4xl mx-auto px-6 py-6" data-id="element-476">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-wrap justify-center gap-4 md:gap-8" data-id="element-477">
          {benefits.map((benefit, index) => <motion.div key={index} variants={itemVariants} className="flex items-center gap-2 text-sm font-medium text-sandev-muted" data-id="element-478">
              <CheckCircle2Icon className="w-4 h-4 text-sandev-accent" data-id="element-479" />
              <span data-id="element-480">{benefit}</span>
            </motion.div>)}
        </motion.div>
      </div>
    </div>;
}
interface ProcessStepProps {
  stepNumber: string;
  title: string;
  description: string;
  icon: any;
  children?: React.ReactNode;
}
function ProcessStep({
  stepNumber,
  title,
  description,
  icon: Icon,
  children
}: ProcessStepProps) {
  return <div className="relative bg-sandev-card border border-sandev-border rounded-lg p-8 overflow-hidden" data-id="element-481">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-sandev-accent to-transparent opacity-50" data-id="element-482"></div>
      <div className="absolute top-4 right-4 text-[120px] font-bold text-sandev-accent opacity-5 leading-none" data-id="element-483">
        {stepNumber}
      </div>
      <div className="relative z-10" data-id="element-484">
        <div className="flex items-start gap-4 mb-6" data-id="element-485">
          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-sandev-accent/10 border border-sandev-accent/30 flex items-center justify-center" data-id="element-486">
            <Icon className="w-6 h-6 text-sandev-accent" data-id="element-487" />
          </div>
          <div data-id="element-488">
            <div className="text-sandev-accent text-sm font-semibold mb-1" data-id="element-489">
              STEP {stepNumber}
            </div>
            <h3 className="text-2xl font-bold text-sandev-text mb-2" data-id="element-490">
              {title}
            </h3>
            <p className="text-sandev-muted text-sm" data-id="element-491">{description}</p>
          </div>
        </div>
        {children && <div className="mt-6" data-id="element-492">{children}</div>}
      </div>
    </div>;
}
interface ServiceCardProps {
  icon: any;
  title: string;
  description: string;
}
function ServiceCard({
  icon: Icon,
  title,
  description
}: ServiceCardProps) {
  return <div className="bg-sandev-bg border border-sandev-border rounded-lg p-6 hover:border-sandev-accent/30 transition-colors" data-id="element-493">
      <div className="flex items-start gap-4" data-id="element-494">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-sandev-accent/10 flex items-center justify-center" data-id="element-495">
          <Icon className="w-5 h-5 text-sandev-accent" data-id="element-496" />
        </div>
        <div data-id="element-497">
          <h4 className="text-sandev-text font-semibold mb-2" data-id="element-498">{title}</h4>
          <p className="text-sandev-muted text-sm" data-id="element-499">{description}</p>
        </div>
      </div>
    </div>;
}
interface PricingItemProps {
  label: string;
  price: string;
  isFree?: boolean;
  description?: string;
}
function PricingItem({
  label,
  price,
  isFree,
  description
}: PricingItemProps) {
  return <div className="bg-sandev-bg border border-sandev-border rounded-lg p-6" data-id="element-500">
      <div className="flex items-center justify-between mb-2" data-id="element-501">
        <h4 className="text-sandev-text font-semibold" data-id="element-502">{label}</h4>
        <div className="flex items-center gap-2" data-id="element-503">
          {isFree && <span className="px-3 py-1 bg-sandev-accent text-white text-xs font-bold rounded-full" data-id="element-504">
              FREE
            </span>}
          <span className={`text-lg font-bold ${isFree ? 'text-sandev-accent' : 'text-sandev-text'}`} data-id="element-505">
            {price}
          </span>
        </div>
      </div>
      {description && <p className="text-sandev-muted text-sm" data-id="element-506">{description}</p>}
    </div>;
}
const faqs = [{
  question: 'What file formats do you accept?',
  answer: 'We primarily work with STL, OBJ, and STEP files for 3D printing. If you only have sketches or ideas, our design service can help create the necessary 3D models for you.'
}, {
  question: "What's the typical turnaround time?",
  answer: "Turnaround times vary based on project complexity, size, and our current queue. Simple prints can often be completed in 2-3 days, while complex design-to-print projects may take 1-2 weeks. We'll provide a specific timeline during your consultation."
}, {
  question: 'Do you offer bulk/repeat orders?',
  answer: "Yes! We can handle small-batch manufacturing and repeat orders. We'll save your optimized print files to ensure consistency across all future batches."
}, {
  question: 'Can I get just a design without printing?',
  answer: 'Absolutely. We offer standalone 3D modeling and design services. We can provide you with the final digital files (STL, STEP, etc.) for you to use or print elsewhere.'
}, {
  question: 'What materials are available?',
  answer: "For FDM printing, we offer PLA, PETG, ABS, TPU (flexible), and carbon-fiber blends. For resin printing, we offer standard, tough, and castable resins. We'll help you choose the best material for your specific application."
}];
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-16 border-t border-sandev-border" data-id="element-507">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.5
    }} data-id="element-508">
        <div className="text-center mb-10" data-id="element-509">
          <h2 className="text-3xl font-bold text-sandev-text mb-4" data-id="element-510">
            Frequently Asked Questions
          </h2>
          <p className="text-sandev-muted" data-id="element-511">
            Everything you need to know about our services and process.
          </p>
        </div>
        <div className="space-y-3" data-id="element-512">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.4
        }} className="bg-sandev-card border border-sandev-border rounded-lg overflow-hidden" data-id="element-513">
              <button onClick={() => toggleFAQ(index)} className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none" data-id="element-514">
                <span className="font-semibold text-sandev-text" data-id="element-515">
                  {faq.question}
                </span>
                <motion.div animate={{
              rotate: openIndex === index ? 180 : 0
            }} transition={{
              duration: 0.2
            }} data-id="element-516">
                  <ChevronDownIcon className="w-5 h-5 text-sandev-muted" data-id="element-517" />
                </motion.div>
              </button>
              <AnimatePresence data-id="element-518">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} data-id="element-519">
                    <div className="px-6 pb-4 text-sandev-muted text-sm" data-id="element-520">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </motion.div>
    </section>;
}
function CTASection() {
  return <section className="py-12 mb-8" data-id="element-521">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-50px'
    }} transition={{
      duration: 0.5
    }} className="relative bg-sandev-card border border-sandev-accent/30 rounded-2xl p-8 md:p-12 text-center overflow-hidden" data-id="element-522">
        {/* Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-md bg-sandev-accent/5 blur-[100px] rounded-full pointer-events-none" data-id="element-523" />
        <div className="relative z-10" data-id="element-524">
          <h2 className="text-3xl md:text-4xl font-bold text-sandev-text mb-4" data-id="element-525">
            Ready to Start Your Project?
          </h2>
          <p className="text-sandev-muted mb-8 max-w-xl mx-auto" data-id="element-526">
            Whether you have a fully prepared 3D model or just an idea sketched
            on a napkin, we're here to help bring it to life.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-527">
            <a href="mailto:Terran@sandevindustries.com" className="w-full sm:w-auto px-8 py-3 bg-sandev-accent hover:bg-sandev-accentHover text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-528">
              <MailIcon className="w-4 h-4" data-id="element-529" />
              Email Us Today
            </a>
            <Link to="/contact" className="w-full sm:w-auto px-8 py-3 bg-sandev-bg hover:bg-sandev-border border border-sandev-border text-sandev-text font-medium rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-530">
              <ArrowRightIcon className="w-4 h-4" data-id="element-531" />
              Contact Us
            </Link>
          </div>
        </div>
      </motion.div>
    </section>;
}
// --- Main Page Component ---
export function PlanOfActionPage() {
  const stepVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <div className="min-h-screen bg-sandev-bg" data-id="element-532">
      {/* Hero Section (Header) */}
      <section className="relative w-full pt-24 pb-16 overflow-hidden border-b border-sandev-border bg-sandev-card" data-id="element-533">
        {/* Geometric Background */}
        <div className="absolute inset-0 z-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)'
      }} data-id="element-534" />
        <div className="max-w-4xl mx-auto px-6 relative z-10" data-id="element-535">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="text-center max-w-3xl mx-auto" data-id="element-536">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="inline-block mb-4 px-3 py-1 rounded-full bg-sandev-accent/10 border border-sandev-accent/30 text-sandev-accent text-xs font-semibold tracking-wider uppercase" data-id="element-537">
              Industrial Prototyping
            </motion.div>
            <h1 className="text-4xl md:text-6xl font-bold text-sandev-text mb-6 leading-tight" data-id="element-538">
              From Concept to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sandev-accent to-blue-500" data-id="element-539">
                Creation
              </span>
            </h1>
            <p className="text-lg text-sandev-muted mb-10 max-w-2xl mx-auto" data-id="element-540">
              Accessible, high-quality 3D printing and design services tailored
              for your unique project needs. We turn your digital ideas into
              physical reality.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-541">
              <a href="#services" className="w-full sm:w-auto px-8 py-3 bg-sandev-bg hover:bg-sandev-border border border-sandev-border text-sandev-text font-medium rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-542">
                View Services
                <ChevronDownIcon className="w-4 h-4" data-id="element-543" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <BenefitsStrip data-id="element-544" />

      <main className="max-w-4xl mx-auto px-6 py-16" data-id="element-545">
        <div id="services" className="space-y-8 scroll-mt-24" data-id="element-546">
          {/* Step 1 */}
          <motion.div variants={stepVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-547">
            <ProcessStep stepNumber="01" title="Choose Your Service" description="Select the service that best fits your project needs" icon={BoxIcon} data-id="element-548">
              <div className="grid md:grid-cols-3 gap-4" data-id="element-549">
                <ServiceCard icon={PrinterIcon} title="3D Printing" description="Bring your designs to life with FDM or resin printing" data-id="element-550" />
                <ServiceCard icon={MessageCircleIcon} title="Consultation" description="Get expert advice on your project's feasibility and approach" data-id="element-551" />
                <ServiceCard icon={PencilRulerIcon} title="Designing" description="Custom 3D modeling and design from concept to file" data-id="element-552" />
              </div>
            </ProcessStep>
          </motion.div>

          {/* Step 2 */}
          <motion.div variants={stepVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-553">
            <ProcessStep stepNumber="02" title="Select Your Materials" description="Choose the right material and finish for your project" icon={PaletteIcon} data-id="element-554">
              <div className="space-y-4" data-id="element-555">
                <ServiceCard icon={PrinterIcon} title="FDM Printing" description="Choose your filament type and color (PLA, PETG, ABS, etc.)" data-id="element-556" />
                <ServiceCard icon={PrinterIcon} title="Resin Printing" description="Select resin type and color for high-detail parts" data-id="element-557" />
                <ServiceCard icon={PencilRulerIcon} title="Design Only" description="Stick with the digital design — no physical print needed" data-id="element-558" />
              </div>
            </ProcessStep>
          </motion.div>

          {/* Step 3 */}
          <motion.div variants={stepVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-559">
            <ProcessStep stepNumber="03" title="Set Your Timeframe" description="Let us know your ideal completion timeline" icon={ClockIcon} data-id="element-560">
              <div className="bg-sandev-bg border border-sandev-border rounded-lg p-6" data-id="element-561">
                <h4 className="text-sandev-text font-semibold mb-3" data-id="element-562">
                  What is your ideal timeframe for completion?
                </h4>
                <p className="text-sandev-muted text-sm" data-id="element-563">
                  Turnaround time depends on project complexity and current
                  queue. We'll provide an accurate estimate during your
                  consultation and work with you to meet your deadlines.
                </p>
              </div>
            </ProcessStep>
          </motion.div>

          {/* Step 4 */}
          <motion.div variants={stepVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-564">
            <ProcessStep stepNumber="04" title="Consultation & Pricing" description="Understanding your project and providing expert guidance" icon={MessageSquareIcon} data-id="element-565">
              <div className="space-y-6" data-id="element-566">
                <div className="space-y-4" data-id="element-567">
                  <PricingItem label="Initial Consultation" price="$0" isFree={true} description="Discuss your project, get feasibility assessment, and receive recommendations" data-id="element-568" />
                  <PricingItem label="Project Work" price="$30/hr" description="Design, modeling, and project development beyond initial consultation" data-id="element-569" />
                  <div className="bg-sandev-accent/10 border border-sandev-accent/30 rounded-lg p-4" data-id="element-570">
                    <p className="text-sandev-accent text-sm font-medium" data-id="element-571">
                      Note: The printing process itself does not include an
                      hourly cost — you only pay for the materials, setup, and a
                      separate runtime cost.
                    </p>
                  </div>
                </div>

                {/* Consultation Format */}
                <div className="bg-sandev-bg border border-sandev-border rounded-lg p-6" data-id="element-572">
                  <h4 className="text-sandev-text font-semibold mb-3" data-id="element-573">
                    How We Meet
                  </h4>
                  <p className="text-sandev-muted text-sm" data-id="element-574">
                    Consultations are conducted via{' '}
                    <span className="text-sandev-text font-medium" data-id="element-575">
                      video call
                    </span>{' '}
                    or{' '}
                    <span className="text-sandev-text font-medium" data-id="element-576">
                      on-site at your location
                    </span>{' '}
                    — whichever works best for your project. We'll coordinate
                    the format when scheduling your consultation.
                  </p>
                </div>

                {/* What Affects Print Cost */}
                <div className="bg-sandev-bg border border-sandev-border rounded-lg p-6" data-id="element-577">
                  <h4 className="text-sandev-text font-semibold mb-4" data-id="element-578">
                    What Affects Print Cost?
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-579">
                    <div className="flex items-start gap-3" data-id="element-580">
                      <LayersIcon className="w-5 h-5 text-sandev-accent shrink-0 mt-0.5" data-id="element-581" />
                      <div data-id="element-582">
                        <span className="text-sandev-text text-sm font-medium block" data-id="element-583">
                          Material Type
                        </span>
                        <span className="text-sandev-muted text-xs" data-id="element-584">
                          Different filaments and resins have different base
                          costs.
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-585">
                      <MaximizeIcon className="w-5 h-5 text-sandev-accent shrink-0 mt-0.5" data-id="element-586" />
                      <div data-id="element-587">
                        <span className="text-sandev-text text-sm font-medium block" data-id="element-588">
                          Part Size & Volume
                        </span>
                        <span className="text-sandev-muted text-xs" data-id="element-589">
                          Larger parts use more material and take longer to
                          print.
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-590">
                      <CpuIcon className="w-5 h-5 text-sandev-accent shrink-0 mt-0.5" data-id="element-591" />
                      <div data-id="element-592">
                        <span className="text-sandev-text text-sm font-medium block" data-id="element-593">
                          Complexity & Detail
                        </span>
                        <span className="text-sandev-muted text-xs" data-id="element-594">
                          Intricate designs may require supports or slower
                          speeds.
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-595">
                      <CopyIcon className="w-5 h-5 text-sandev-accent shrink-0 mt-0.5" data-id="element-596" />
                      <div data-id="element-597">
                        <span className="text-sandev-text text-sm font-medium block" data-id="element-598">
                          Quantity
                        </span>
                        <span className="text-sandev-muted text-xs" data-id="element-599">
                          Batch orders can reduce per-unit cost through
                          optimized layouts.
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 md:col-span-2" data-id="element-600">
                      <WrenchIcon className="w-5 h-5 text-sandev-accent shrink-0 mt-0.5" data-id="element-601" />
                      <div data-id="element-602">
                        <span className="text-sandev-text text-sm font-medium block" data-id="element-603">
                          Post-Processing
                        </span>
                        <span className="text-sandev-muted text-xs" data-id="element-604">
                          Sanding, painting, or assembly adds to the final cost.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Quote CTA */}
                <div className="bg-sandev-card border border-sandev-accent/30 rounded-lg p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-605">
                  <div data-id="element-606">
                    <h4 className="text-sandev-text font-semibold mb-1" data-id="element-607">
                      Get a Custom Quote
                    </h4>
                    <p className="text-sandev-muted text-sm" data-id="element-608">
                      Every project is unique — get a personalized quote
                      tailored to your specific needs.
                    </p>
                  </div>
                  <Link to="/contact" className="shrink-0 px-6 py-2.5 bg-sandev-accent hover:bg-sandev-accentHover text-white text-sm font-semibold rounded-lg transition-all shadow-[0_0_15px_rgba(0,180,216,0.3)] hover:shadow-[0_0_25px_rgba(0,180,216,0.5)]" data-id="element-609">
                    Request Quote
                  </Link>
                </div>
              </div>
            </ProcessStep>
          </motion.div>

          {/* Step 5 */}
          <motion.div variants={stepVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-610">
            <ProcessStep stepNumber="05" title="Product, Payment & Delivery" description="Receive your finished product and complete your order" icon={PackageIcon} data-id="element-611">
              <div className="space-y-6" data-id="element-612">
                <div className="bg-sandev-bg border border-sandev-border rounded-lg p-6" data-id="element-613">
                  <h4 className="text-sandev-text font-semibold mb-3" data-id="element-614">
                    Payment Methods
                  </h4>
                  <div className="flex flex-wrap gap-3" data-id="element-615">
                    <span className="px-4 py-2 bg-sandev-card border border-sandev-border rounded-lg text-sandev-muted text-sm" data-id="element-616">
                      e-Transfer
                    </span>
                    <span className="px-4 py-2 bg-sandev-card border border-sandev-border rounded-lg text-sandev-muted text-sm" data-id="element-617">
                      PayPal
                    </span>
                    <span className="px-4 py-2 bg-sandev-card border border-sandev-border rounded-lg text-sandev-muted text-sm" data-id="element-618">
                      Direct Bank Transfer
                    </span>
                    <span className="px-4 py-2 bg-sandev-card border border-sandev-border rounded-lg text-sandev-muted text-sm" data-id="element-619">
                      Cheque
                    </span>
                  </div>
                </div>
                <div className="bg-sandev-accent/10 border border-sandev-accent/30 rounded-lg p-4" data-id="element-620">
                  <p className="text-sandev-accent text-sm font-medium" data-id="element-621">
                    A 50% refundable deposit is required to begin work on your
                    project. The remaining balance is due upon completion and
                    delivery.
                  </p>
                </div>
                <div className="bg-sandev-bg border border-sandev-border rounded-lg p-6" data-id="element-622">
                  <h4 className="text-sandev-text font-semibold mb-3" data-id="element-623">
                    Delivery Options
                  </h4>
                  <p className="text-sandev-muted text-sm" data-id="element-624">
                    Choose between local delivery or shipping arrangements.
                    We'll coordinate the best delivery method for your project
                    during the consultation process.
                  </p>
                </div>
              </div>
            </ProcessStep>
          </motion.div>
        </div>

        <FAQSection data-id="element-625" />
        <CTASection data-id="element-626" />
      </main>
    </div>;
}